// LiveDebatePlayer — partner-debate live-stream player.
//
// Behaviour:
//   • Reads the configured Facebook video URL from /api/live-debate.
//     Empty URL → component renders NOTHING (so the slot is invisible
//     when no debate is scheduled).
//   • Inline: banner image on top + 16:9 Facebook plugin iframe below.
//   • Persistent mini-player: an IntersectionObserver watches the slot.
//     When the user scrolls past, a CSS class fixes the player to the
//     bottom-right of the viewport (or bottom strip on mobile). The
//     iframe element NEVER changes src or remounts, so playback is
//     uninterrupted. Close (X) dismisses the mini-player until the
//     user scrolls back into the slot.
//   • The slot reserves its inline height (measured once) as a
//     placeholder while mini, so the page above doesn't jump when the
//     player floats out of flow.
//
// Editor flow: paste a Facebook URL or <iframe> embed snippet at
// /admin/live-debate. The component fetches whatever's saved on mount.

import { useEffect, useRef, useState } from 'react';
import './LiveDebatePlayer.css';

const ENDPOINT = '/api/live-debate';
const BANNER = '/assets/chamber-lovin-debate.jpg';

/** Build the Facebook plugin URL for an iframe `src`. If the editor
 *  already pasted a plugin URL (full src from an embed snippet), pass it
 *  through unchanged so their width/parameters are preserved. */
function buildPluginUrl(stored: string): string {
  const url = stored.trim();
  if (!url) return '';
  if (url.includes('facebook.com/plugins/video.php')) return url;
  const params = new URLSearchParams({
    href: url,
    show_text: 'false',
    autoplay: 'true',
    width: '900',
  });
  return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
}

export default function LiveDebatePlayer() {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isMini, setIsMini] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [reservedHeight, setReservedHeight] = useState<number | null>(null);
  const slotRef = useRef<HTMLDivElement | null>(null);

  // Fetch the configured video URL on mount.
  useEffect(() => {
    let cancelled = false;
    fetch(ENDPOINT, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { url: '' }))
      .then((data: unknown) => {
        if (cancelled) return;
        const next =
          data && typeof data === 'object' && 'url' in data &&
          typeof (data as { url: unknown }).url === 'string'
            ? (data as { url: string }).url
            : '';
        setUrl(next);
      })
      .catch(() => {
        /* fail silently — render nothing on error */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Observe the slot. Mini state activates when the slot has been
  // scrolled PAST (its top edge is above the viewport). Returning to
  // the slot deactivates mini.
  useEffect(() => {
    const slot = slotRef.current;
    if (!slot || !url) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          setIsMini(true);
        } else if (entry.isIntersecting) {
          setIsMini(false);
        }
      },
      { threshold: 0 },
    );
    observer.observe(slot);
    return () => observer.disconnect();
  }, [url]);

  // Re-show the mini-player whenever the slot comes back into view, so
  // dismissing only sticks for the current "scrolled-away" period.
  useEffect(() => {
    if (!isMini) setDismissed(false);
  }, [isMini]);

  // Measure the slot's inline height once, while inline, so the slot
  // can reserve that space when the player floats out (mini).
  useEffect(() => {
    if (isMini || !url) return;
    const slot = slotRef.current;
    if (!slot) return;
    const h = slot.offsetHeight;
    if (h > 0) setReservedHeight(h);
  }, [isMini, url]);

  if (loading || !url) return null;

  const pluginUrl = buildPluginUrl(url);
  const showMini = isMini && !dismissed;

  return (
    <div
      ref={slotRef}
      className="live-debate-slot"
      style={
        showMini && reservedHeight ? { minHeight: reservedHeight } : undefined
      }
    >
      <div className={`live-debate${showMini ? ' is-mini' : ''}`}>
        <img
          className="live-debate__banner"
          src={BANNER}
          alt="Chamber × Lovin Malta debate"
        />
        <div className="live-debate__frame">
          <iframe
            className="live-debate__iframe"
            src={pluginUrl}
            title="Live debate"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            scrolling="no"
          />
        </div>
        {showMini && (
          <button
            type="button"
            className="live-debate__close"
            onClick={() => setDismissed(true)}
            aria-label="Hide mini player"
            title="Hide mini player"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
